import { wallPivotLift } from '../../core/hp/parseProject';
import { formatFloat, type ProjectFile, ProjectFileError, type ProjectElement } from '../../core/hp/projectFile';
import type { Item, Level, Scene, Vec2, Wall } from '../../core/hp/types';
import { containsPoint, sameYaw } from '../../state/sceneGesture';
import { LIVE_PROPS_IN_APP, livePieceRefusal, recolours, sameJson } from '../patch';
import type { DocumentPatch } from '../protocol';
import { catalogFurnitureXml, rewriteFurnitureXml, type FurnitureChange } from './furnitureXml';
import type { RtEvent } from './wire';

/** Where an item the web adds in a live room came from: a catalog tile (its PRO mark, its first variation) or a copy of the item with IR id `copyOf`. */
export type ItemOrigin = { kind: 'catalog'; pro: boolean; variationId?: string } | { kind: 'copy'; copyOf: string };
/** A removed piece as the file held it, so that undoing the delete adds it back under its own UniqueId. */
export type GraveyardEntry = { xml: string; parentId: string; variationPath: string };
export type RefusalCode = 'forbidden' | 'busy' | 'conflict' | 'invalid';
/** `pieces`: the captions of the pieces the refusal is about, for its words. */
export type Refusal = { code: RefusalCode; reason: string; pieces?: string[] };
/** `buried`: each piece the events delete, by UniqueId, as the file holds it before them. `held`: the captions of the pieces an undo or redo leaves as they are (`EncodeInput.models`). */
export type Encoded = { events: RtEvent[]; buried: Array<[string, GraveyardEntry]>; held?: string[] };

export interface EncodeInput {
  file: ProjectFile;
  /** The scene parsed from `file`, the one the patch was made against. */
  scene: Scene;
  patch: DocumentPatch;
  /** By the added item's UniqueId. */
  origins: ReadonlyMap<string, ItemOrigin>;
  /** By UniqueId. */
  graveyard: ReadonlyMap<string, GraveyardEntry>;
  /** The parser's `catalogPivots`. */
  pivots?: ReadonlyMap<string, number>;
  /**
   * By UniqueId, the model the piece wore when the file last held it whole; one wearing another now keeps the old size there,
   * so a whole resend of it is refused `busy`, naming it, or left out of an undo or redo (`history`, `Encoded.held`).
   */
  models?: ReadonlyMap<string, string>;
  history?: boolean;
}

const NO_ID = '#WebLiveNoId', ONLY_IN_APP = '#WebLiveOnlyInApp', CONFLICT = '#WebLiveConflict', OUTSIDE_FACE = '#WebLiveOutsideFace';
export const CHANGED_IN_APP = '#WebLiveChangedInApp';
const APP_FIELDS = ['light', 'modules', 'placement', 'pivot', 'caption', 'price', 'url'] as const;
const DEG = Math.PI / 180;

class Refused extends Error {
  constructor(readonly code: RefusalCode, readonly reason: string, readonly pieces?: string[]) {
    super(reason);
  }
}

function refuse(reason: string, code: RefusalCode = 'invalid', pieces?: string[]): never {
  throw new Refused(code, reason, pieces);
}

function conflict(): never {
  throw new Refused('conflict', CONFLICT);
}

interface Context {
  file: ProjectFile;
  level: Level;
  pivots?: ReadonlyMap<string, number>;
  holders: ReadonlyMap<string, number>;
  models?: ReadonlyMap<string, string>;
  /** Captions of the pieces left as they are for wearing another model than the file's whole copy of them. */
  left: string[];
  events: RtEvent[];
}

type Point = { x: number; y: number };

const change = (uid: string, property: string, value: string): RtEvent => ({ type: 'DomainChange', key: `${uid}::${property}`, data: { value } });
const removal = (uid: string): RtEvent => ({ type: 'DomainLifecycle', key: uid, data: { op: 'remove' } });
const addition = (uid: string, parentId: string, xml: string, variationPath: string): RtEvent =>
  ({ type: 'DomainLifecycle', key: uid, data: { op: 'add', parentId, kind: 'Furniture', xml, variationPath } });
const json = (point: Point) => `{"x":${formatFloat(point.x)},"y":${formatFloat(point.y)}}`;
const norm360 = (degrees: number) => ((degrees % 360) + 360) % 360;
const basename = (raw: string) => raw.slice(raw.lastIndexOf('/') + 1);
const sameFloat = (held: number, next: number) => Math.fround(held) === Math.fround(next);
/** The file keeps an angle to 0.01°. */
const sameAngle = (held: number, next: number) => { const turn = norm360(held - next); return turn < 0.005 || turn > 359.995; };
const along = (wall: Wall, p: Vec2) => (p.x - wall.center.x) * wall.dir.x + (p.z - wall.center.z) * wall.dir.z;
/** The plane-local point the file holds for a floor or ceiling item: the ceiling plane is the floor flipped over. */
const filePoint = (item: Item): Point => ({ x: item.placement === 'ceiling' ? -item.position.x : item.position.x, y: -item.position.z });

/** The file's Rotation for an IR yaw: the parser's `itemYaw` undone to 0.01°, mirrored on the ceiling. */
function fileRotation(item: Item): number {
  const theta = norm360(Math.round(norm360(item.rotation / DEG - 180) * 100) / 100);
  return item.placement === 'ceiling' ? norm360(-theta) : theta;
}

/** The app's DomainChange / DomainLifecycle events for the web's own item edit, none for a value the file already holds, or why the app alone can make it. Never changes `file`. */
export function encodeProposal(input: EncodeInput): Encoded | Refusal {
  try {
    return encode(input);
  } catch (error) {
    if (error instanceof Refused) return { code: error.code, reason: error.reason, ...(error.pieces ? { pieces: error.pieces } : {}) };
    if (error instanceof ProjectFileError) {
      return error.code === 'removed' || error.code === 'not-found' ? { code: 'conflict', reason: CONFLICT } : { code: 'invalid', reason: ONLY_IN_APP };
    }
    throw error;
  }
}

function encode({ file, scene, patch, origins, graveyard, pivots, models, history }: EncodeInput): Encoded {
  if (patch.kind === 'replace' || patch.meta || patch.name) refuse(ONLY_IN_APP);
  if (patch.floors.some(floor => floor.architectureAfter !== undefined)) refuse('#WebLiveItemsOnly');
  const holders = new Map<string, number>();
  for (const level of scene.levels) {
    for (const { uniqueId } of [level, ...level.rooms, ...level.walls, ...level.openings, ...level.items]) {
      if (uniqueId) holders.set(uniqueId, (holders.get(uniqueId) ?? 0) + 1);
    }
  }
  const events: RtEvent[] = [], buried: Array<[string, GraveyardEntry]> = [], left: string[] = [];
  for (const floor of patch.floors) {
    // By its id, not its slot: a co-author's floor added or removed before it since the patch was made shifts the slots.
    const floorId = floor.architectureBefore.uniqueId ?? refuse(NO_ID);
    const ctx: Context = { file, level: scene.levels.find(level => level.uniqueId === floorId) ?? conflict(), pivots, holders, models, left, events };
    for (const { before, after } of floor.items) {
      if (before && after) {
        if (!sameJson(before, after)) edit(ctx, before, after);
      } else if (before) {
        const { uid, element } = held(ctx, before), parentId = planeOf(file, element).attribute('UniqueId');
        events.push(removal(uid));
        if (parentId) buried.push([uid, { xml: file.xmlOf(element), parentId, variationPath: variationPath(file, element) }]);
      } else if (after) {
        add(ctx, after, origins, graveyard);
      }
    }
  }
  if (left.length && !history) refuse(CHANGED_IN_APP, 'busy', left);
  return { events, buried, ...(left.length ? { held: left } : {}) };
}

export const furnitureIdOf = (file: ProjectFile, element: ProjectElement): string => file.children(element, 'Info')[0]?.attribute('FurnitureId') ?? '';

/** Whether the app rebuilt the piece from another model since the file last held it whole (`EncodeInput.models`). */
function replaced(ctx: Context, uid: string, model: string): boolean {
  const whole = ctx.models?.get(uid);
  return whole !== undefined && whole !== model;
}

/** The item's element, refused unless the scene holds it as the patch saw it and the file holds it under its one exact UniqueId. */
function held(ctx: Context, item: Item): { uid: string; element: ProjectElement } {
  const current = ctx.level.items.find(each => each.id === item.id) ?? conflict();
  const uid = item.uniqueId;
  if (!uid || item.id !== `i:${uid}` || ctx.holders.get(uid) !== 1) refuse(NO_ID);
  if (!sameJson(current, item)) conflict();
  const element = ctx.file.byId(uid);
  if (!element || element.tag !== 'Furniture') refuse(NO_ID);
  return { uid, element };
}

function edit(ctx: Context, before: Item, after: Item): void {
  const { uid, element } = held(ctx, before), { file } = ctx, recoloured = recolours(before, after);
  if (after.uniqueId !== uid) refuse(ONLY_IN_APP);
  const refusal = livePieceRefusal(before, after);
  if (refusal) refuse(refusal);
  if (APP_FIELDS.some(field => !sameJson(before[field], after[field])) || (recoloured && !after.variationId)) refuse(ONLY_IN_APP);
  const moved = before.position.x !== after.position.x || before.position.z !== after.position.z;
  const raised = before.elevation !== after.elevation, turned = !sameYaw(before.rotation, after.rotation);
  const onWall = after.placement === 'wall', rewalled = onWall && after.wallId !== before.wallId;
  if (onWall && turned && !rewalled) {
    // Rebased, a piece keeps its depth from the face: carried onto the outer face, it arrives as the turn that faces it out.
    const wall = facedWall(ctx, after);
    if (sameYaw(after.rotation, Math.atan2(wall.normalIn.x, -wall.normalIn.z))) refuse(OUTSIDE_FACE);
    refuse('#WebLiveWallRotateInApp');
  }

  let to: Point | undefined, parentId: string | undefined;
  if (onWall && (moved || rewalled || raised)) {
    const wall = rewalled ? facedWall(ctx, after) : wallOf(ctx, after), point = heldPosition(file, element);
    // Y moves by the elevation delta: re-deriving it through the pivot rule would undo the parser's clamp.
    to = { x: moved || rewalled ? along(wall, after.position) : point.x, y: point.y + (after.elevation - before.elevation) };
    if (rewalled) parentId = frontId(file, wall);
  } else if (moved) {
    const plane = planeOf(file, element), next = landing(ctx, after, plane);
    to = filePoint(after);
    if (next && next !== plane) parentId = planeId(file, next);
  }
  if (parentId !== undefined) {
    if (replaced(ctx, uid, furnitureIdOf(file, element))) { ctx.left.push(before.caption ?? ''); return; }
    const xml = rewriteFurnitureXml(file.xmlOf(element), { position: to, rotation: turned && !onWall ? fileRotation(after) : undefined,
      planeOffset: raised && !onWall ? after.elevation : undefined });
    ctx.events.push(removal(uid), addition(uid, parentId, xml, variationPath(file, element)));
  } else {
    const position = heldPosition(file, element);
    if (to && !(sameFloat(position.x, to.x) && sameFloat(position.y, to.y))) ctx.events.push(change(uid, 'Position', json(to)));
    if (raised && !onWall && !sameFloat(heldNumber(element, 'PlaneOffset'), after.elevation)) ctx.events.push(change(uid, 'PlaneOffset', formatFloat(after.elevation)));
    if (turned && !sameAngle(heldNumber(element, 'Rotation'), fileRotation(after))) ctx.events.push(change(uid, 'Rotation', formatFloat(fileRotation(after))));
  }
  if (recoloured) ctx.events.push(change(uid, 'Variation', `${file.children(element, 'Info')[0]?.attribute('FurnitureId') ?? after.modelRef.raw}@${after.variationId}`));
  if (!!before.locked !== !!after.locked) ctx.events.push(change(uid, 'Locked', after.locked ? 'True' : 'False'));
}

function add(ctx: Context, item: Item, origins: ReadonlyMap<string, ItemOrigin>, graveyard: ReadonlyMap<string, GraveyardEntry>): void {
  const uid = item.uniqueId;
  if (!uid || item.id !== `i:${uid}`) refuse(ONLY_IN_APP);
  if (ctx.file.byId(uid)) conflict();
  const buried = graveyard.get(uid);
  if (buried) {
    if (replaced(ctx, uid, buried.variationPath.slice(0, buried.variationPath.lastIndexOf('@')))) { ctx.left.push(item.caption ?? ''); return; }
    if (!ctx.file.byId(buried.parentId)) conflict();
    ctx.events.push(addition(uid, buried.parentId, buried.xml, buried.variationPath));
    return;
  }
  const origin = origins.get(uid) ?? refuse(ONLY_IN_APP);
  const event = origin.kind === 'catalog' ? catalogAddition(ctx, uid, item, origin) : copyAddition(ctx, uid, item, origin.copyOf);
  if (event) ctx.events.push(event);
}

function catalogAddition(ctx: Context, uid: string, item: Item, origin: { pro: boolean; variationId?: string }): RtEvent {
  const furnitureId = item.modelRef.raw, infoId = origin.variationId ?? item.variationId ?? basename(furnitureId);
  let { h, d } = item.size, parentId: string, position: Point, rotation = 0, planeOffset = 0;
  if (item.placement === 'wall') {
    const wall = facedWall(ctx, item);
    parentId = frontId(ctx.file, wall);
    position = { x: along(wall, item.position), y: item.elevation - wallPivotLift(item.modelRef, h, item.scale[1], ctx.pivots).lift };
  } else {
    parentId = planeId(ctx.file, landing(ctx, item) ?? refuse(ONLY_IN_APP));
    position = filePoint(item);
    rotation = fileRotation(item);
    planeOffset = item.placement === 'ceiling' ? ctx.level.height - h - item.elevation : item.elevation;
    // The parser lays these models flat on the ceiling by swapping height and depth, so the file holds them standing.
    if (item.placement === 'ceiling' && item.tags?.includes('AutorotateVerticalToHorizontal')) [h, d] = [d, h];
  }
  const [sx, sy, sz] = item.scale.map(Math.abs);
  const xml = catalogFurnitureXml({ uniqueId: uid, size: { w: item.size.w / sx, h: h / sy, d: d / sz }, rotation, planeOffset, position, scale: item.scale,
    pro: origin.pro, info: { id: infoId, furnitureId, price: item.price, caption: item.caption } });
  return addition(uid, parentId, xml, `${furnitureId}@${infoId}`);
}

function copyAddition(ctx: Context, uid: string, item: Item, copyOf: string): RtEvent | null {
  const source = ctx.level.items.find(each => each.id === copyOf) ?? conflict();
  const { uid: sourceUid, element } = held(ctx, source), { file } = ctx;
  if (replaced(ctx, sourceUid, furnitureIdOf(file, element))) { ctx.left.push(source.caption ?? ''); return null; }
  const { id, uniqueId, position, rotation, elevation, wallId } = source;
  if (!sameJson({ ...item, id, uniqueId, position, rotation, elevation, wallId }, source)) conflict();
  const rewrite: FurnitureChange = { uniqueId: uid };
  let parentId: string;
  if (item.placement === 'wall') {
    const wall = facedWall(ctx, item);
    parentId = frontId(file, wall);
    rewrite.position = { x: along(wall, item.position), y: heldPosition(file, element).y + (item.elevation - elevation) };
  } else {
    if (item.placement === 'ceiling' && item.elevation !== elevation) refuse(LIVE_PROPS_IN_APP);
    const plane = planeOf(file, element);
    parentId = planeId(file, landing(ctx, item, plane) ?? plane);
    rewrite.position = filePoint(item);
    if (item.rotation !== rotation) rewrite.rotation = fileRotation(item);
    if (item.elevation !== elevation) rewrite.planeOffset = item.elevation;
  }
  return addition(uid, parentId, rewriteFurnitureXml(file.xmlOf(element), rewrite), variationPath(file, element));
}

function wallOf(ctx: Context, item: Item): Wall {
  return ctx.level.walls.find(each => each.id === item.wallId) ?? conflict();
}

/** The wall an item is put on, refused when the item stands on its outer face, which the parser does not show. */
function facedWall(ctx: Context, item: Item): Wall {
  const wall = wallOf(ctx, item), p = item.position;
  if ((p.x - wall.center.x) * wall.normalIn.x + (p.z - wall.center.z) * wall.normalIn.z < 0) refuse(OUTSIDE_FACE);
  return wall;
}

/** The Floor or Ceiling of the smallest room holding the item's point, `current` on a tie; nothing outside every room. */
function landing(ctx: Context, item: Item, current?: ProjectElement): ProjectElement | undefined {
  const holding = ctx.level.rooms.filter(room => containsPoint(room.polygon, item.position));
  if (!holding.length) return undefined;
  const area = Math.min(...holding.map(room => room.area)), home = current && ctx.file.parent(current);
  const rooms = holding.filter(room => room.area === area).map(room => (room.uniqueId ? ctx.file.byId(room.uniqueId) : undefined));
  const room = rooms.find(each => each && each === home) ?? rooms[0];
  if (!room || room.tag !== 'Room') refuse(NO_ID);
  return ctx.file.children(room, item.placement === 'ceiling' ? 'Ceiling' : 'Floor')[0] ?? refuse(NO_ID);
}

function planeOf(file: ProjectFile, element: ProjectElement): ProjectElement {
  const collection = file.parent(element), plane = collection && file.parent(collection);
  return plane ?? conflict();
}

function planeId(file: ProjectFile, plane: ProjectElement | undefined): string {
  const id = plane?.attribute('UniqueId');
  if (!plane || !id || file.byId(id) !== plane) refuse(NO_ID);
  return id;
}

function frontId(file: ProjectFile, wall: Wall): string {
  const element = wall.uniqueId ? file.byId(wall.uniqueId) : undefined;
  return planeId(file, element?.tag === 'Wall' ? file.children(element, 'Front')[0] : undefined);
}

/** The element's numeric attribute, 0 where the file leaves it out. */
function heldNumber(element: ProjectElement, name: string): number {
  const value = Number(element.attribute(name) ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function heldPosition(file: ProjectFile, element: ProjectElement): Point {
  const position = file.children(element, 'Position')[0];
  const axis = (name: 'x' | 'y') => { const value = Number(position?.attribute(name) ?? position?.attribute(name.toUpperCase()) ?? 0); return Number.isFinite(value) ? value : 0; };
  return { x: axis('x'), y: axis('y') };
}

/** The `FurnitureId@Id` the piece wears in the file. */
function variationPath(file: ProjectFile, element: ProjectElement): string {
  const furnitureId = furnitureIdOf(file, element);
  return `${furnitureId}@${file.children(element, 'Info')[0]?.attribute('Id') ?? basename(furnitureId)}`;
}
